/* ─────────────────────────────────────────────────────────────────────────────
 * ⚠ THIS FILE IS A COPY. THE ORIGINAL LIVES IN THE WEB REPO:
 *     HypeTunes_Web_2026/src/components/three/faceRig.js
 *
 * The mouth driver for bodies that carry the ICT face (53 ARKit-named shapes).
 * One implementation, shared like faceDecal/faceMount: the World runs it on its
 * residents (voice chat + Living City lines), the website on its viewers.
 *
 * ⚠ DO NOT EDIT BELOW THE SENTINEL. Fix the Web original, then:
 *     node scripts/check_face_decal_sync.mjs --sync
 * ───────────────────────────────────────────────────────────────────────────── */

/* ── VERBATIM FROM HypeTunes_Web_2026 BELOW THIS LINE ── */
/**
 * THE MOUTH THAT MOVES — a voice drives the face's own shapes.
 *
 * Bodies upgraded with the ICT face (docs/3d_body_renewal/FINDINGS.md §5–8 in
 * the API repo) carry 53 ARKit-named morph targets — jawOpen, mouthFunnel,
 * eyeBlink_L, … — on meshes that share the body's skeleton. Nothing moves them
 * on its own. This does, from ONE number: how loud the voice is right now.
 *
 * ── WHY LOUDNESS AND NOT WORDS ─────────────────────────────────────────────
 * Every voice here is ElevenLabs audio that is already playing, and the
 * renderer already reads its level (the World's `talkBridge.amplitude`). Driving
 * the jaw from that costs no extra request, no forced alignment and no added
 * delay, and it works for a song as well as a sentence. Exact per-word shapes
 * (ElevenLabs `/with-timestamps` returns timings IN THE SAME REQUEST) can be
 * layered on later through `setVisemes`-style input; nothing here assumes it.
 *
 * ── WHAT IT ADDS TO LOUDNESS ───────────────────────────────────────────────
 * A jaw that only opens and shuts reads as a puppet. Real speech also rounds
 * the lips (oo/oh), spreads them (ee), and closes them between syllables, so
 * two slow independent wobbles mix those shapes in proportion to the level —
 * never on silence. And a face that never blinks reads as a mannequin even at
 * rest, so blinks run whether or not anyone is talking.
 *
 * ⚠ THE SHAPES ARE SET BY NAME ON EVERY MESH THAT HAS THEM. The face, the
 * teeth/gums and any future part each carry their own morphTargetDictionary;
 * a name a mesh lacks is skipped, so a body can ship fewer shapes safely.
 * ⚠ A BODY WITH NO FACE SHAPES GETS `null` — callers keep their old path (the
 * video decal) for it, untouched.
 */

/** Every mesh under `model` that carries the face's shapes (keyed by jawOpen). */
export function findFaceShapes(model) {
  const meshes = []
  model?.traverse((o) => {
    const dict = o.morphTargetDictionary
    if (o.isMesh && dict && dict.jawOpen != null && o.morphTargetInfluences) meshes.push(o)
  })
  return meshes
}

/** True when the body has its own moving face — the old video decal must not mount on top of it. */
export const hasFaceShapes = (model) => findFaceShapes(model).length > 0

const SHAPES = ['jawOpen', 'mouthClose', 'mouthFunnel', 'mouthPucker', 'mouthSmile_L', 'mouthSmile_R',
  'mouthStretch_L', 'mouthStretch_R', 'mouthLowerDown_L', 'mouthLowerDown_R', 'mouthUpperUp_L', 'mouthUpperUp_R',
  'mouthPress_L', 'mouthPress_R', 'browInnerUp', 'eyeBlink_L', 'eyeBlink_R']

/**
 * @param {THREE.Object3D} model  a body already in the scene
 * @param {{ gain?: number, seed?: number }} opts  gain scales how wide the mouth opens; seed de-syncs a crowd
 * @returns {{ update(dt:number, level:number, talking:boolean):void, reset():void, meshes:THREE.Mesh[] } | null}
 */
export function createMouthDriver(model, { gain = 1, seed = Math.random() * 100 } = {}) {
  const meshes = findFaceShapes(model)
  if (!meshes.length) return null
  const slots = meshes.map((m) => SHAPES.map((name) => m.morphTargetDictionary[name] ?? -1))
  const w = Object.fromEntries(SHAPES.map((n) => [n, 0]))
  let t = seed
  let open = 0                 // the jaw, with its own attack/release
  let prevLevel = 0
  let press = 0                // a brief lip press when the voice drops out between syllables
  let nextBlink = 1.5 + (seed % 3)
  let blinkT = -1

  const write = () => {
    for (let m = 0; m < meshes.length; m++) {
      const inf = meshes[m].morphTargetInfluences; const s = slots[m]
      for (let k = 0; k < SHAPES.length; k++) if (s[k] >= 0) inf[s[k]] = w[SHAPES[k]]
    }
  }
  const ease = (cur, target, rate, dt) => cur + (target - cur) * Math.min(1, rate * dt)

  return {
    meshes,
    /**
     * @param dt       seconds since the last frame
     * @param level    0..1 — how loud the voice is right now (already smoothed or raw; both work)
     * @param talking  the coarse gate: false eases every speech shape back to rest
     */
    update(dt, level, talking) {
      dt = Math.min(0.1, Math.max(0, dt || 0)); t += dt
      /* ⚠ GATED AND CURVED. Speech is mostly quiet with short loud peaks; mapped
         straight, the jaw sat a third open through every quiet stretch and read as
         gaping. Below the gate the mouth closes; above it opens on a soft curve. */
      const raw = talking ? Math.max(0, Math.min(1, (level || 0) * gain)) : 0
      const L = Math.pow(Math.max(0, (raw - 0.12) / 0.88), 0.9)

      /* Jaw: opens fast (a consonant-to-vowel is ~40ms), closes nearly as fast so
         syllables separate. ⚠ CAPPED AT 0.42: ICT's jawOpen 1.0 is a yawn, and real
         talking rarely drops the jaw past a third of that. */
      open = ease(open, L * 0.42, L > open ? 25 : 16, dt)

      // A sharp drop in level while talking is the gap between syllables: press the lips briefly.
      if (talking && prevLevel - L > 0.12) press = 1
      press = Math.max(0, press - dt * 9)
      prevLevel = L

      // Two slow, unrelated wobbles choose how rounded vs spread the mouth is — scaled by the level.
      const round = Math.max(0, Math.sin(t * 3.1) * 0.6 + Math.sin(t * 1.27 + 1.3) * 0.4)
      const spread = Math.max(0, Math.sin(t * 2.3 + 2.1) * 0.6 + Math.sin(t * 0.9 + 0.4) * 0.4)
      const vow = Math.min(1, L * 1.6)
      const tgt = {
        jawOpen: open,
        mouthClose: talking ? Math.max(0, 0.18 - open) * 0.6 : 0,
        mouthFunnel: round * vow * 0.45,
        mouthPucker: round * vow * 0.2,
        mouthSmile_L: spread * vow * 0.22, mouthSmile_R: spread * vow * 0.22,
        mouthStretch_L: spread * vow * 0.18, mouthStretch_R: spread * vow * 0.18,
        mouthLowerDown_L: open * 0.55, mouthLowerDown_R: open * 0.55,
        mouthUpperUp_L: open * 0.22, mouthUpperUp_R: open * 0.22,
        mouthPress_L: press * 0.5, mouthPress_R: press * 0.5,
        browInnerUp: Math.max(0, L - 0.55) * 0.5,           // the brows lift only on emphasis
      }
      for (const k of Object.keys(tgt)) w[k] = ease(w[k], tgt[k], talking ? 18 : 8, dt)

      // Blinks, always: every 2–6s, ~150ms, now and then a double.
      nextBlink -= dt
      if (blinkT < 0 && nextBlink <= 0) { blinkT = 0; nextBlink = 2 + ((t * 7.3 + seed) % 4) }
      let b = 0
      if (blinkT >= 0) {
        blinkT += dt
        b = blinkT < 0.07 ? blinkT / 0.07 : blinkT < 0.15 ? 1 - (blinkT - 0.07) / 0.08 : 0
        if (blinkT >= 0.15) { blinkT = -1; if (((t * 13.7) % 1) < 0.15) nextBlink = 0.18 }
      }
      /* ⚠ THE LIDS REST SLIGHTLY LOWERED. Fully open lids over a whole iris read
         as startled on every face we tried; ~25% is a relaxed, attentive look. */
      const lid = Math.max(0.25, b)
      w.eyeBlink_L = lid; w.eyeBlink_R = lid
      write()
    },
    reset() { for (const k of SHAPES) w[k] = 0; w.eyeBlink_L = w.eyeBlink_R = 0.25; open = 0; press = 0; write() },
  }
}

export default createMouthDriver
